// Admin-only global config page — three Layerd-side jsonb sections:
//   - bmEngine                  (BM-Engine HTTP URLs + cache TTL)
//   - bmS3Export                (training-data writer config)
//   - verdictCache              (mode/version/dualWrite/syncIntervalSecs)
//
// Each emits the matching RELOAD_* event on save so Layerd reloads.
import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Grid,
  Typography,
  TextField,
  CircularProgress,
  Box,
  Select,
  Button,
  MenuItem,
} from "@mui/material";

import adminAxios from "../../../utils/axios/v1/adminAxios";
import { IOSSwitch, SnackbarAlert } from "../../../components/pages/common/styled";
import { VerdictCacheMode } from "../../../utils/constants";
import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import { TOKENS, SectionCard } from "../../../components/pages/_new/super/design";

const FieldLabel = ({ children }) => (
  <Typography
    sx={{ fontSize: 12.5, fontWeight: 600, color: TOKENS.fg2, mb: 0.5 }}
  >
    {children}
  </Typography>
);

function SectionWithSave({ title, description, children, onSave, dirty, saving }) {
  return (
    <SectionCard
      title={title}
      description={description}
      action={
        <Button
          onClick={onSave}
          disabled={!dirty || saving}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: "#fff",
            background: TOKENS.primary,
            borderRadius: `${TOKENS.radiusXs}px`,
            px: 2,
            "&:hover": { background: TOKENS.primaryHover },
            "&.Mui-disabled": { color: "#fff", opacity: 0.5 },
          }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      }
    >
      {children}
    </SectionCard>
  );
}

export default function GlobalConfig() {
  const [loaded, setLoaded] = React.useState(false);

  // bmEngine
  const [apiUrl, setApiUrl] = React.useState("");
  const [polyJsUrl, setPolyJsUrl] = React.useState("");
  const [polyJsCache, setPolyJsCache] = React.useState(60);
  const [bmDirty, setBmDirty] = React.useState(false);
  const [bmSaving, setBmSaving] = React.useState(false);

  // bmS3Export — store as raw JSON text since the schema is open-ended
  const [s3Json, setS3Json] = React.useState("{}");
  const [s3Dirty, setS3Dirty] = React.useState(false);
  const [s3Saving, setS3Saving] = React.useState(false);

  // verdictCache
  const [vcMode, setVcMode] = React.useState(VerdictCacheMode.OFF);
  const [vcVersion, setVcVersion] = React.useState("v1");
  const [vcDualWrite, setVcDualWrite] = React.useState(false);
  const [vcSync, setVcSync] = React.useState(10);
  const [vcDirty, setVcDirty] = React.useState(false);
  const [vcSaving, setVcSaving] = React.useState(false);

  const [snackOpen, setSnackOpen] = React.useState(false);
  const [snackMsg, setSnackMsg] = React.useState("");
  const [snackSev, setSnackSev] = React.useState("info");
  const toast = (sev, msg) => {
    setSnackSev(sev);
    setSnackMsg(msg);
    setSnackOpen(true);
  };

  React.useEffect(() => {
    let cancelled = false;
    Promise.all([
      adminAxios.get(`/global-config/bm-engine`).catch(() => ({ data: {} })),
      adminAxios.get(`/global-config/bm-s3-export`).catch(() => ({ data: {} })),
      adminAxios.get(`/global-config/verdict-cache`).catch(() => ({ data: {} })),
    ]).then(([be, s3, vc]) => {
      if (cancelled) return;
      setApiUrl(be.data?.apiUrl || "");
      setPolyJsUrl(be.data?.polymorphicJsUrl || "");
      setPolyJsCache(be.data?.polymorphicJsCacheSeconds ?? 60);
      setS3Json(JSON.stringify(s3.data || {}, null, 2));
      setVcMode(vc.data?.mode || VerdictCacheMode.OFF);
      setVcVersion(vc.data?.version || "v1");
      setVcDualWrite(!!vc.data?.dualWrite);
      setVcSync(vc.data?.syncIntervalSecs ?? 10);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onSaveBm = async () => {
    setBmSaving(true);
    try {
      const body = {
        apiUrl,
        polymorphicJsUrl: polyJsUrl,
        polymorphicJsCacheSeconds: Number(polyJsCache),
      };
      await adminAxios.put(`/global-config/bm-engine`, body);
      toast("success", "BM-Engine config saved (RELOAD_BM_ENGINE_CONFIG emitted)");
      setBmDirty(false);
    } catch (e) {
      toast("error", e?.message || String(e));
    } finally {
      setBmSaving(false);
    }
  };

  const onSaveS3 = async () => {
    setS3Saving(true);
    try {
      let body;
      try {
        body = JSON.parse(s3Json);
      } catch (e) {
        toast("error", `Invalid JSON: ${e.message}`);
        setS3Saving(false);
        return;
      }
      if (typeof body !== "object" || Array.isArray(body)) {
        toast("error", "Body must be a JSON object");
        setS3Saving(false);
        return;
      }
      await adminAxios.put(`/global-config/bm-s3-export`, body);
      toast("success", "BM S3 export config saved (RELOAD_BM_S3_CONFIG emitted)");
      setS3Dirty(false);
    } catch (e) {
      toast("error", e?.message || String(e));
    } finally {
      setS3Saving(false);
    }
  };

  const onSaveVc = async () => {
    setVcSaving(true);
    try {
      const body = {
        mode: vcMode,
        version: vcVersion,
        dualWrite: vcDualWrite,
        syncIntervalSecs: Number(vcSync),
      };
      await adminAxios.put(`/global-config/verdict-cache`, body);
      toast("success", "Verdict cache config saved (RELOAD_VERDICT_CACHE_CONFIG emitted)");
      setVcDirty(false);
    } catch (e) {
      toast("error", e?.message || String(e));
    } finally {
      setVcSaving(false);
    }
  };

  if (!loaded) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Helmet title="Global Config (admin)" />
      <PageHeader
        title="Global Config (admin)"
        description="Three Layerd-side jsonb sections — each save fires the matching pg_notify event."
      />

      <Box sx={{ display: "grid", gap: 2 }}>
        <SectionWithSave
          title="BM-Engine HTTP client"
          description="Endpoints used to call the Bot Management engine."
          onSave={onSaveBm}
          dirty={bmDirty}
          saving={bmSaving}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FieldLabel>API URL</FieldLabel>
              <TextField
                fullWidth
                size="small"
                value={apiUrl}
                placeholder="http://10.0.0.1:8000"
                onChange={(e) => {
                  setApiUrl(e.target.value);
                  setBmDirty(true);
                }}
              />
              <Typography sx={{ fontSize: 11.5, color: TOKENS.fg3, mt: 0.5 }}>
                Empty = engine disabled (FP-POST falls back to local UUID mint).
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FieldLabel>Polymorphic JS URL</FieldLabel>
              <TextField
                fullWidth
                size="small"
                value={polyJsUrl}
                placeholder="http://10.0.0.1:8000/api/v1/bm/poly_js"
                onChange={(e) => {
                  setPolyJsUrl(e.target.value);
                  setBmDirty(true);
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FieldLabel>Polymorphic JS cache (seconds)</FieldLabel>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 0 }}
                value={polyJsCache}
                onChange={(e) => {
                  setPolyJsCache(e.target.value);
                  setBmDirty(true);
                }}
              />
            </Grid>
          </Grid>
        </SectionWithSave>

        <SectionWithSave
          title="BM S3 export (training-data writer)"
          description="JSON object. Typical keys: bucket, prefix, region, credentials, buffer, manifest. PUT merges with existing."
          onSave={onSaveS3}
          dirty={s3Dirty}
          saving={s3Saving}
        >
          <TextField
            fullWidth
            multiline
            minRows={10}
            value={s3Json}
            onChange={(e) => {
              setS3Json(e.target.value);
              setS3Dirty(true);
            }}
            sx={{
              "& textarea": {
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: 13,
              },
            }}
          />
        </SectionWithSave>

        <SectionWithSave
          title="Verdict cache (WAF L1/L2/L3)"
          description="Bump version to invalidate L2. Sync interval drives async writers."
          onSave={onSaveVc}
          dirty={vcDirty}
          saving={vcSaving}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FieldLabel>Mode</FieldLabel>
              <Select
                fullWidth
                size="small"
                value={vcMode}
                onChange={(e) => {
                  setVcMode(e.target.value);
                  setVcDirty(true);
                }}
              >
                <MenuItem value={VerdictCacheMode.OFF}>Off — bypass cache</MenuItem>
                <MenuItem value={VerdictCacheMode.SHADOW}>Shadow — write only</MenuItem>
                <MenuItem value={VerdictCacheMode.ACTIVE}>Active — full L1→L2→L3</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} md={3}>
              <FieldLabel>Version (bump invalidates L2)</FieldLabel>
              <TextField
                fullWidth
                size="small"
                value={vcVersion}
                onChange={(e) => {
                  setVcVersion(e.target.value);
                  setVcDirty(true);
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FieldLabel>Dual-write</FieldLabel>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  border: `1px solid ${TOKENS.border}`,
                  borderRadius: `${TOKENS.radiusXs}px`,
                  px: 1.5,
                  py: "8px",
                  background: TOKENS.bgSurface,
                }}
              >
                <Typography sx={{ flex: 1, fontSize: 13, color: TOKENS.fg1 }}>
                  {vcDualWrite ? "Enabled" : "Disabled"}
                </Typography>
                <IOSSwitch
                  checked={vcDualWrite}
                  onChange={() => {
                    setVcDualWrite(!vcDualWrite);
                    setVcDirty(true);
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <FieldLabel>Sync interval (s, 1-3600)</FieldLabel>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 1, max: 3600 }}
                value={vcSync}
                onChange={(e) => {
                  setVcSync(e.target.value);
                  setVcDirty(true);
                }}
              />
            </Grid>
          </Grid>
        </SectionWithSave>
      </Box>
      <SnackbarAlert
        open={snackOpen}
        onClose={() => setSnackOpen(false)}
        severity={snackSev}
        message={snackMsg}
      />
    </React.Fragment>
  );
}
